import { axiom } from '../lib/axiom';
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { subscribeToAuthChanges, signInWithGoogle, sendEmailSignInLink, completeEmailSignIn, signOut, User, isFirebaseConfigured } from '../lib/firebase';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface AccessInfo {
  hasAccess: boolean;
  accessLevel: 'none' | 'waitlist' | 'invited' | 'approved' | 'admin';
  message?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  accessInfo: AccessInfo | null;
  accessLoading: boolean;
  configError: string | null;
  signInWithGoogle: () => Promise<{ user: User | null; error: Error | null }>;
  sendEmailLink: (email: string) => Promise<{ success: boolean; error: Error | null }>;
  signOut: () => Promise<{ success: boolean; error: Error | null }>;
  checkAccess: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Export for test providers to use the same context
export { AuthContext };

// Export types for test providers
export type { AuthContextType, AccessInfo };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(isFirebaseConfigured); // Start loading if Firebase is configured
  const [accessInfo, setAccessInfo] = useState<AccessInfo | null>(null);
  const [accessLoading, setAccessLoading] = useState(isFirebaseConfigured); // Start loading if Firebase is configured
  const configError = isFirebaseConfigured ? null : 'Firebase is not configured. Please set VITE_FIREBASE_* environment variables.';

  // Check access from backend
  const checkAccess = async (currentUser?: User | null) => {
    const userToCheck = currentUser ?? user;
    if (!userToCheck) {
      setAccessInfo(null);
      return;
    }

    setAccessLoading(true);
    try {
      const token = await userToCheck.getIdToken();
      const response = await fetch(`${API_URL}/auth/check-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email: userToCheck.email }),
      });

      if (response.ok) {
        const data = await response.json();
        setAccessInfo(data);
      } else {
        setAccessInfo({ hasAccess: false, accessLevel: 'none', message: 'Failed to verify access' });
      }
    } catch (error) {
      axiom.error('Access check failed', { error: error instanceof Error ? error.message : String(error) });
      setAccessInfo({ hasAccess: false, accessLevel: 'none', message: 'Network error' });
    } finally {
      setAccessLoading(false);
    }
  };

  // Log sign-in to backend
  const logSignIn = async (user: User) => {
    try {
      const token = await user.getIdToken();
      await fetch(`${API_URL}/auth/log-signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          providerId: user.providerData[0]?.providerId || 'unknown',
        }),
      });
    } catch (error) {
      axiom.error('Failed to log sign-in', { error: error instanceof Error ? error.message : String(error) });
    }
  };

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(async (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);

      if (firebaseUser) {
        // Set Sentry user context for error tracking
        Sentry.setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email || undefined,
        });

        await logSignIn(firebaseUser);
        await checkAccess(firebaseUser);
      } else {
        // Clear Sentry user context on sign out
        Sentry.setUser(null);

        setAccessInfo(null);
        setAccessLoading(false);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Proactive token refresh - refresh 5 minutes before expiry
  useEffect(() => {
    if (!user) return;

    const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes
    const RETRY_DELAY_MS = 60 * 1000; // 1 minute
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    let isCancelled = false;

    const scheduleTokenRefresh = async () => {
      if (isCancelled) return;

      try {
        // Get the current token result to check expiration
        const tokenResult = await user.getIdTokenResult();
        if (isCancelled) return; // Check again after async call

        const expirationTime = new Date(tokenResult.expirationTime).getTime();
        const now = Date.now();
        const timeUntilExpiry = expirationTime - now;
        const refreshTime = Math.max(0, timeUntilExpiry - TOKEN_REFRESH_BUFFER_MS);

        axiom.info('[Auth] Token refresh scheduled', { expires_in_s: Math.round(timeUntilExpiry / 1000), refresh_in_s: Math.round(refreshTime / 1000) });

        refreshTimer = setTimeout(async () => {
          if (isCancelled) return; // Guard against stale timer

          try {
            axiom.info('[Auth] Proactively refreshing token...');
            await user.getIdToken(true); // Force refresh
            if (isCancelled) return; // Don't schedule next refresh if cancelled

            axiom.info('[Auth] Token refreshed successfully');
            // Schedule next refresh
            scheduleTokenRefresh();
          } catch (error) {
            if (isCancelled) return; // Don't retry if cancelled

            axiom.error('[Auth] Failed to refresh token', { error: error instanceof Error ? error.message : String(error) });
            // Retry in 1 minute on error
            refreshTimer = setTimeout(() => {
              if (!isCancelled) scheduleTokenRefresh();
            }, RETRY_DELAY_MS);
          }
        }, refreshTime);
      } catch (error) {
        if (isCancelled) return;

        axiom.error('[Auth] Failed to schedule token refresh', { error: error instanceof Error ? error.message : String(error) });
        // Retry initial schedule attempt after delay
        refreshTimer = setTimeout(() => {
          if (!isCancelled) scheduleTokenRefresh();
        }, RETRY_DELAY_MS);
      }
    };

    scheduleTokenRefresh();

    return () => {
      isCancelled = true;
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }
    };
  }, [user]);

  // Handle email link sign-in on mount
  useEffect(() => {
    if (window.location.pathname === '/auth/verify') {
      completeEmailSignIn().then(({ user: signedInUser, error }) => {
        if (signedInUser) {
          window.location.href = '/dashboard';
        } else if (error) {
          axiom.error('Email sign-in failed', { error: error instanceof Error ? error.message : String(error) });
        }
      });
    }
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    accessInfo,
    accessLoading,
    configError,
    signInWithGoogle,
    sendEmailLink: sendEmailSignInLink,
    signOut,
    checkAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
